import axios from "axios";
import { captureCustomizerException, incrementCustomizerMetric } from "./sentry";
import { safeGetItem, safeGetJson, safeRemoveItem, safeSetJson } from "./safeStorage";

export class DraftService {
    /**
     * Lädt Draft-Daten vom Backend
     * @param {string} draftId - Draft ID aus URL Parameter
     * @returns {Promise<Object>} Draft data plus privacy-safe request diagnostics.
     */
    static async loadDraftData(draftId) {
        const backendUrl = process.env.VUE_APP_DRAFT_API_URL;

        try {
            if (!backendUrl) {
                if (process.env.VUE_APP_DEV_MODE === "true") {
                    console.error("❌ [DraftService] VUE_APP_DRAFT_API_URL not configured");
                }
                return {
                    success: false,
                    diagnostics: {
                        requestUrl: "",
                        httpStatus: null,
                        axiosCode: null,
                    },
                    error: {
                        type: 'CONFIG_ERROR',
                        details: 'VUE_APP_DRAFT_API_URL environment variable is not set'
                    }
                };
            }
            
            const response = await axios.post(backendUrl, {
                draftId: draftId
            }, {
                // sentry-ignore: reported with draft context by the catch below (captureCustomizerException)
                sentryReport: false,
                headers: {
                    "Content-Type": "application/json",
                    "X-Requested-With": "XMLHttpRequest",
                },
                timeout: 30000
            });
            
            const draftData = getDraftPayloadFromResponse(response.data);
            
            if (!draftData) {
                return {
                    success: false,
                    diagnostics: {
                        requestUrl: backendUrl,
                        httpStatus: response.status ?? 200,
                        axiosCode: null,
                    },
                    error: {
                        type: 'INVALID_RESPONSE',
                        details: response.data
                    }
                };
            }

            return {
                success: true,
                data: draftData,
                diagnostics: {
                    requestUrl: backendUrl,
                    httpStatus: response.status ?? 200,
                    axiosCode: null,
                },
            };
            
        } catch (error) {
            let errorType = 'UNKNOWN_ERROR';
            if (error.response) {
                switch (error.response.status) {
                    case 404:
                        errorType = 'DRAFT_NOT_FOUND';
                        break;
                    case 400:
                        errorType = 'BAD_REQUEST';
                        break;
                    case 500:
                        errorType = 'SERVER_ERROR';
                        break;
                    default:
                        errorType = 'HTTP_ERROR';
                }
            } else if (error.code === 'ECONNABORTED') {
                errorType = 'TIMEOUT_ERROR';
            } else if (error.code === 'NETWORK_ERROR') {
                errorType = 'NETWORK_ERROR';
            }
            
            // A 404 means the link points to a draft that does not exist (mostly bots
            // opening made-up links). Counted, not an issue.
            if (errorType === 'DRAFT_NOT_FOUND') {
                incrementCustomizerMetric("customizer.draft_not_found", {
                    host: window.location.host,
                });
            }

            if (process.env.VUE_APP_DEV_MODE === "true") {
                console.error("❌ [DraftService] Draft loading failed:", {
                    hasDraftId: Boolean(draftId),
                    errorType: errorType,
                    errorCode: error.code || "unknown",
                    status: error.response?.status,
                    statusText: error.response?.statusText,
                });
            }
            
            if (errorType !== 'DRAFT_NOT_FOUND') {
                captureCustomizerException(error, {
                    tags: {
                        component: "DraftService",
                        action: "loadDraftData",
                        errorType,
                        host: window.location.host,
                    },
                    context: {
                        hasDraftId: Boolean(draftId),
                        status: error.response?.status,
                        statusText: error.response?.statusText,
                    },
                });
            }
            
            return {
                success: false,
                diagnostics: {
                    requestUrl: error.config?.url || backendUrl || "",
                    httpStatus: error.response?.status || null,
                    axiosCode: error.code || null,
                },
                error: {
                    type: errorType,
                    details: error.response ? {
                        status: error.response.status,
                        statusText: error.response.statusText,
                        data: error.response.data
                    } : error.message
                }
            };
        }
    }
    
    /**
     * Extrahiert draft-id aus URL (einfache Version)
     * @returns {string|null} - Draft ID oder null wenn nicht vorhanden
     */
    static getDraftIdFromUrl() {
        const hash = window.location.hash;
        if (hash && hash.includes('?')) {
            const hashParams = new URLSearchParams(hash.split('?')[1]);
            const hashDraftId = hashParams.get('draft-id');
            if (hashDraftId) {
                return acceptDraftId(hashDraftId);
            }
        }
        
        const urlParams = new URLSearchParams(window.location.search);
        const urlDraftId = urlParams.get('draft-id');
        if (urlDraftId) {
            return acceptDraftId(urlDraftId);
        }
        return null;
    }
}

export default DraftService;

// Real draft ids are plain letters/digits. Anything else (e.g. "abc)**" copied out of
// Markdown text) can never match a draft, so it is not sent to the backend.
const DRAFT_ID_PATTERN = /^[A-Za-z0-9_-]+$/;

function acceptDraftId(draftId) {
    if (DRAFT_ID_PATTERN.test(draftId)) {
        return draftId;
    }
    incrementCustomizerMetric("customizer.draft_id_rejected", {
        host: window.location.host,
    });
    return null;
}

/**
 * Extracts the actual draft payload from backend responses.
 * Backend endpoints return a single-item array: [{ ...payload }]
 * @param {any} responseData
 * @returns {Object|null}
 */
export function getDraftPayloadFromResponse(responseData) {
    if (Array.isArray(responseData)) {
        return responseData.find((entry) => entry && typeof entry === "object") || null;
    }

    if (responseData && typeof responseData === "object") {
        return responseData;
    }

    return null;
}

const DRAFT_META_KEY = 'currentDraftMeta';
let draftIdCache = { href: null, draftId: null };

/**
 * Returns draft-id from URL hash/search or from persisted localStorage meta (with migration from legacy key)
 * @returns {string|null}
 */
export function getDraftIdFromContext() {
    try {
        const meta = getDraftMeta();
        if (meta?.draftId) {
            return meta.draftId;
        }
        
        const legacyDraftId = safeGetItem('currentDraftId');
        if (legacyDraftId) {
            storeDraftMeta(null, legacyDraftId);
            return legacyDraftId;
        }
        
        return DraftService.getDraftIdFromUrl();
    } catch (_error) {
        // sentry-ignore: falls back to the draft id in the URL
        return DraftService.getDraftIdFromUrl();
    }
}

/**
 * Simple boolean helper to check if we have any draft context
 * @returns {boolean}
 */
export function hasDraftIdInContext() {
    return !!getDraftIdFromContext();
}

/**
 * Boolean helper to check for draft-id in URL only (no localStorage fallback)
 */
export function hasDraftIdInUrl() {
    return !!DraftService.getDraftIdFromUrl();
}

/**
 * Cached draft-id lookup (URL-scoped; recalculates when location changes)
 */
export function getDraftIdFromUrlCached() {
    const href = window.location.href;
    if (draftIdCache.href === href) {
        return draftIdCache.draftId;
    }
    const draftId = DraftService.getDraftIdFromUrl();
    draftIdCache = { href, draftId };
    return draftId;
}

export function clearDraftIdCache() {
    draftIdCache = { href: null, draftId: null };
}

/**
 * Persist draft meta (bookId + draftId) for sharing/change-requests
 */
export function storeDraftMeta(bookId, draftId) {
    if (!draftId) return;
    safeSetJson(DRAFT_META_KEY, { bookId: bookId || null, draftId });
    safeRemoveItem('currentDraftId');
}

/**
 * Get stored draft meta if available
 */
export function getDraftMeta() {
    try {
        return safeGetJson(DRAFT_META_KEY, null);
    } catch (_error) {
        // sentry-ignore: safeGetJson already reports storage problems
        return null;
    }
}

/**
 * Clear stored draft meta + legacy id
 */
export function clearDraftMeta() {
    safeRemoveItem(DRAFT_META_KEY);
    safeRemoveItem('currentDraftId');
}

/**
 * Helper function for redirecting to step 1 of customizer
 * @param {string} _reason - Reason for redirect (for logging)
 * @param {Object} pageSettings - PageSettings object with step1Url
 */
// eslint-disable-next-line no-unused-vars
export function redirectToStep1(reason = 'Unknown error', pageSettings = null) {
    // Counted, not an issue: missing step data is often expected (old links, cleared storage).
    // Unexpected causes are reported as errors where they happen.
    incrementCustomizerMetric("customizer.redirected_to_step1", {
        reason: String(reason).slice(0, 60),
    });

    clearDraftMeta();
    
    if (!pageSettings || !pageSettings.step1Url) {
        const fallbackUrl = window.location.origin;
        window.location.href = fallbackUrl;
        return;
    }
    
    if (process.env.VUE_APP_DEV_MODE === "true") {
        const currentPath = window.location.pathname;
        const bookIdMatch = currentPath.match(/\/(\d+)\//);
        const bookId = bookIdMatch ? bookIdMatch[1] : null;

        if (bookId) {
            const newUrl = `${window.location.origin}/${bookId}/step1`;
            window.location.href = newUrl;
            return;
        }
    }
    
    const baseUrl = window.location.origin + window.location.pathname + '#';
    const step1Path = pageSettings.step1Url.startsWith('/') ? pageSettings.step1Url : '/' + pageSettings.step1Url;
    const newUrl = baseUrl + step1Path;
    
    window.location.href = newUrl;
}

/**
 * Helper function to validate if draft data is usable
 * @param {Object} draftData - Draft data to validate
 * @returns {boolean} - True if draft data has pages or song output (works for both books and songs)
 */
export function isDraftDataValid(draftData) {
    const payload = getDraftPayloadFromResponse(draftData);
    const hasPages = !!(payload && payload.pages && Object.keys(payload.pages).length > 0);
    
    const hasSongOutput = !!(
        payload &&
        payload.output &&
        payload.output.song &&
        Object.keys(payload.output.song).length > 0
    );
    
    const isValid = hasPages || hasSongOutput;
    
    return isValid;
}
